import type { SelectedOptions } from '@corratech/pylot-variant-selector'
import type { UseOptionSelectorReturn } from '@corratech/pylot-variant-selector/src/hooks/useOptionSelector'
import type { Option } from '@pylot-data/hooks/product/use-product-ui'
import { OptionType } from '@pylot-data/hooks/product/use-product-ui'
import {
    COLOR_SWATCH_DATA,
    IMAGE_SWATCH_DATA,
    TEXT_SWATCH_DATA
} from '@corratech/pylot-variant-selector/src/hooks/useOptionSelector/utils/getOptionType'
import { useTranslation } from 'next-i18next/pages'
import type { ReactElement } from 'react'
import React, { useEffect } from 'react'
import Swatch from '../Swatch'
import s from './SwatchGroup.module.scss'
import cn from 'classnames'
import {
    ConfigurableProductOptionsValues,
    Maybe,
    ImageSwatchData,
    ColorSwatchData,
    TextSwatchData
} from '@pylot-data/pylotschema'
import Link from 'next/link'

type SwatchGroupProps = Omit<UseOptionSelectorReturn, 'options'> & {
    option: Option
    isCircle?: boolean
    disableAll?: boolean
    displaySwatchOptionLabel?: boolean
    disabledValues?: string[]
    setImage?: React.Dispatch<React.SetStateAction<string>>
    colorsImagesMap?: Map<string, string>
    displayTallaSwatch?: boolean
    restriction?: number
    productURL?: string
    /**
     * Renderiza tambien las opciones no disponibles, disabled y tachadas,
     * en vez de omitirlas. EVO-245.
     */
    showUnavailableOptions?: boolean
}

type ConfigurableProductOptions = ConfigurableProductOptionsValues & {
    swatch_data?: Maybe<ImageSwatchData | ColorSwatchData | TextSwatchData>
}

export const SwatchGroup = ({
    option,
    selectedOptions,
    isCircle = true,
    disableAll = false,
    displaySwatchOptionLabel = false,
    disabledValues = [],
    setSelectedOptions,
    setImage,
    colorsImagesMap,
    displayTallaSwatch,
    restriction,
    productURL,
    showUnavailableOptions = false
}: SwatchGroupProps): ReactElement => {
    const { t } = useTranslation('common')

    const active = selectedOptions[option.code as keyof SelectedOptions] ?? ''

    const validValues =
        option.values
            ?.map((v) => v.label!)
            .filter((v) => !disabledValues.includes(v)) ?? []

    const normalizedActive = validValues.includes(active)
        ? active
        : (validValues[0] ?? '')

    useEffect(() => {
        if (active && !validValues.includes(active)) {
            setSelectedOptions((oldSelectedOptions) => ({
                ...oldSelectedOptions,
                [option.code]: normalizedActive
            }))
        }
    }, [active, normalizedActive, validValues, option.code, setSelectedOptions])

    if (option.type === OptionType.Dropdown) {
        return (
            <div className={cn('swatch-group-wrap', s['swatch-group'])}>
                {}
                <select
                    className="form-select block appearance-none w-full bg-grey-lighter border-2 border-grey-lighter text-p-1 py-3 px-4 pr-8 rounded focus:shadow-none focus:border-accents-2 max-w-sm"
                    id="grid-state"
                    value={normalizedActive}
                    onChange={(e) => {
                        const img = colorsImagesMap?.get(e.target?.value)
                        if (img) {
                            setImage?.(img)
                        }
                        setSelectedOptions((oldSelectedOptions) => ({
                            ...oldSelectedOptions,
                            [option.code]: e.target?.value ?? ''
                        }))
                    }}
                >
                    <option value="">{t('Select an option...')}</option>
                    {option.values!.map((v, i: number) => {
                        const value = v.label!
                        const isUnavailable = disabledValues.includes(value)

                        if (isUnavailable && !showUnavailableOptions)
                            return null

                        return (
                            <option
                                disabled={disableAll || isUnavailable}
                                key={`${v.value_index ?? value}-${i}`}
                                value={value}
                            >
                                {value}
                            </option>
                        )
                    })}
                </select>
            </div>
        )
    }

    return (
        <div className={cn('swatch-group-wrap', s['swatch-group'])}>
            {option
                .values!.slice(0, restriction)
                .map((v: ConfigurableProductOptions, i: number) => {
                    if (
                        v.swatch_data?.__typename === TEXT_SWATCH_DATA &&
                        !displayTallaSwatch
                    )
                        return null

                    const color =
                        v.swatch_data?.__typename === COLOR_SWATCH_DATA
                            ? v.swatch_data?.value
                            : ''
                    const url =
                        v.swatch_data?.__typename === IMAGE_SWATCH_DATA
                            ? v.swatch_data?.thumbnail
                            : ''

                    const value = v.label!

                    const isUnavailable = disabledValues.includes(value)

                    if (isUnavailable && !showUnavailableOptions) return null

                    return (
                        <Swatch
                            key={`${v.value_index}-${i}`}
                            active={value === normalizedActive}
                            disabled={disableAll || isUnavailable}
                            color={color!}
                            label={value}
                            displaySwatchOptionLabel={displaySwatchOptionLabel}
                            optionType={option.type}
                            isCircle={isCircle}
                            url={url!}
                            onClick={
                                isUnavailable
                                    ? undefined
                                    : () => {
                                          const img =
                                              colorsImagesMap?.get(value)
                                          if (img) {
                                              setImage?.(img)
                                          }
                                          setSelectedOptions(
                                              (oldSelectedOptions) => {
                                                  const newValue =
                                                      option.type ===
                                                      OptionType.TextSwatch
                                                          ? (v.swatch_data
                                                                ?.value ??
                                                            value)
                                                          : value

                                                  return {
                                                      ...oldSelectedOptions,
                                                      [option.code]: newValue!
                                                  }
                                              }
                                          )
                                      }
                            }
                        />
                    )
                })}
            {restriction &&
            restriction > 0 &&
            option.values!.length > restriction ? (
                <div className={cn('my-2', 'restricted-options')}>
                    <span>
                        <Link href={productURL!}>
                            + {option.values!.length - restriction}
                        </Link>
                    </span>
                </div>
            ) : null}
        </div>
    )
}
